import React, { useState } from 'react';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import { useAppContext } from '../../../context/AppContext';
import SafeIcon from '../../../common/SafeIcon';
import { Button } from '../../../components/ui/Button';
import { motion } from 'framer-motion';

export default function ManageUsers() {
  const { allUsers, updateProfile } = useAppContext();
  const [editingUser, setEditingUser] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleEdit = (user) => {
    setEditingUser({ ...user });
    setSaveSuccess(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const { error } = await updateProfile(editingUser, editingUser.id);
    setIsSaving(false);
    
    if (error) {
      alert(error.message);
    } else {
      setSaveSuccess(true);
      setTimeout(() => {
        setEditingUser(null);
        setSaveSuccess(false);
      }, 1500);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingUser(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  return (
    <DashboardLayout>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Users</h1>
          <p className="text-gray-500 mt-1">Manage and edit all registered profiles.</p>
        </div>
        <div className="bg-indigo-50 px-4 py-2 rounded-xl border border-indigo-100">
          <span className="text-indigo-700 font-bold text-sm">{allUsers.length} Total Users</span>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase">User</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {allUsers.map((u) => (
                <tr key={u.id} className="group hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <img src={u.avatar_url} className="w-10 h-10 rounded-full object-cover border shadow-sm" alt="" />
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-gray-900 text-sm">{u.first_name} {u.last_name}</h3>
                          {u.is_admin && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[8px] font-bold rounded uppercase">Admin</span>}
                        </div>
                        <p className="text-xs text-gray-400">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase inline-block w-fit ${u.subscription_status === 'active' || u.is_lifetime ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {u.is_lifetime ? 'Lifetime Pro' : (u.subscription_status === 'active' ? 'Active Pro' : 'Free Plan')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => handleEdit(u)} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                        <SafeIcon name="Edit2" size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editingUser && (
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-white rounded-[2.5rem] w-full max-w-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 sticky top-0 z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Edit User Profile</h2>
                <p className="text-sm text-gray-500">{editingUser.email}</p>
              </div>
              <button onClick={() => setEditingUser(null)} className="p-3 hover:bg-white rounded-full transition-colors shadow-sm">
                <SafeIcon name="X" />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-8 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">First Name</label>
                  <input name="first_name" value={editingUser.first_name || ''} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Last Name</label>
                  <input name="last_name" value={editingUser.last_name || ''} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>

              <div className="p-6 bg-gray-50 rounded-3xl border border-gray-100 space-y-4">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest">Contact & Address Info</h3>
                <div className="grid grid-cols-2 gap-4">
                  <input name="phone_mobile" value={editingUser.phone_mobile || ''} onChange={handleChange} placeholder="Mobile Phone" className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none bg-white" />
                  <input name="phone_business" value={editingUser.phone_business || ''} onChange={handleChange} placeholder="Business Phone" className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none bg-white" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <input name="city" value={editingUser.city || ''} onChange={handleChange} placeholder="City" className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none bg-white" />
                  <input name="state" value={editingUser.state || ''} onChange={handleChange} placeholder="State" className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none bg-white" />
                  <input name="zip" value={editingUser.zip || ''} onChange={handleChange} placeholder="Zip" className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none bg-white" />
                </div>
              </div>

              <div className="p-6 bg-indigo-50 rounded-3xl border border-indigo-100 space-y-4">
                <h3 className="text-xs font-bold text-indigo-900 uppercase tracking-widest">Administrative Permissions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center gap-3 p-4 bg-white rounded-xl border border-indigo-100 cursor-pointer hover:bg-indigo-50 transition-colors">
                    <input type="checkbox" name="is_lifetime" checked={editingUser.is_lifetime || false} onChange={handleChange} className="w-5 h-5 rounded text-indigo-600" />
                    <div>
                      <p className="text-sm font-bold text-gray-900">Lifetime Pro</p>
                      <p className="text-[10px] text-gray-500">Unlocks all features.</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-4 bg-white rounded-xl border border-indigo-100 cursor-pointer hover:bg-indigo-50 transition-colors">
                    <input type="checkbox" name="is_admin" checked={editingUser.is_admin || false} onChange={handleChange} className="w-5 h-5 rounded text-purple-600" />
                    <div>
                      <p className="text-sm font-bold text-gray-900">Platform Admin</p>
                      <p className="text-[10px] text-gray-500">Full system access.</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <Button 
                  type="submit" 
                  className={`flex-1 py-4 text-lg rounded-2xl transition-all ${saveSuccess ? 'bg-green-600 hover:bg-green-600' : ''}`} 
                  disabled={isSaving || saveSuccess}
                >
                  {isSaving ? 'Saving...' : (saveSuccess ? 'Changes Saved!' : 'Update User Profile')}
                </Button>
                <Button type="button" variant="secondary" onClick={() => setEditingUser(null)} className="px-8 rounded-2xl">Cancel</Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </DashboardLayout>
  );
}
